// src/studio/components/TableSectionView.jsx
//
// A TABLE section's content_markdown is a JSON array of row objects (see
// jade_context_builder.parse_table_content's docstring) — not markdown pipe
// syntax despite the field name. This renders that JSON as a grid keyed by
// the section's own declared `columns`, so an unexpected/missing field in a
// row can't shift the table out of alignment.
import PropTypes from "prop-types";

function parseRows(raw) {
  if (!raw) return [];
  let text = raw.trim();
  if (!text) return [];
  if (text.startsWith("```")) {
    text = text.split("\n").filter((l) => !l.trim().startsWith("```")).join("\n").trim();
  }
  try {
    const parsed = JSON.parse(text);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list.filter((r) => r && typeof r === "object");
  } catch {
    return null; // signals "couldn't parse", distinct from "parsed to zero rows"
  }
}

function humanize(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TableSectionView({ content, columns }) {
  const rows = parseRows(content);

  if (rows === null) {
    return <p className="text-base text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
      This section&rsquo;s content isn&rsquo;t valid table data — try regenerating it.
    </p>;
  }
  if (rows.length === 0) {
    return <p className="text-base text-slate-400 italic">No rows generated for this section yet.</p>;
  }

  const cols = columns?.length ? columns : Object.keys(rows[0]);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {cols.map((c) => (
              <th key={c} className="px-4 py-2.5 text-left font-semibold text-slate-600 whitespace-nowrap">
                {humanize(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {cols.map((c) => (
                <td key={c} className="px-4 py-2.5 text-slate-700 border-b border-gray-100 align-top">
                  {row[c] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

TableSectionView.propTypes = {
  content: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.string),
};
